'use client';

import { useLoginState } from '@packages/shared/src/hooks/useLoginState/useLoginState';
import { useMediaType } from '@packages/shared/src/hooks/useMediaType/useMediaType';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { getBridge } from '@packages/shared/src/utils/getBridge';
import type { GlycerinCartProduct } from '@packages/tracking/src/types/types';
import type { GTMEventGlycerinDisplayCart } from '@packages/tracking/src/types/events';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import { LineItemFragmentFragmentDoc } from '@packages/gql/generated/shopping/LineItemFragmentFragmentDoc';
import { BasketFragmentFragmentDoc } from '@packages/gql/generated/shopping/BasketFragmentFragmentDoc';
import { buildGlycerinCartProduct } from '@packages/modules/src/helpers/buildGlycerinObject/buildGlycerinObject';
import { LoginDialogButton } from '@packages/modules/src/Login/LoginDialog/LoginDialogButton.client';
import { BasketEmpty } from './BasketEmpty.client';
import { DirectOrder } from '../DirectOrder/DirectOrder';
import { BasketLineItem } from '../LineItem/BasketLineItem.client';
import { BuyBox } from '../BuyBox/BuyBox';
import { JoeLoyalty } from '../JoeLoyalty/JoeLoyalty';
import { PaybackBox } from '../PaybackBox/PaybackBox';
import { useEventTracking } from '../Tracking/useEventTracking';
import { Seals } from '../Seals/Seals';

export interface BasketProps {
  data?: FragmentType<typeof BasketFragmentFragmentDoc>;
}

export const Basket = ({ data }: BasketProps) => {
  const fragmentData = getFragmentData(BasketFragmentFragmentDoc, data);
  const { formatMessage } = useIntl();
  const { loggedIn } = useLoginState();
  const isDesktop = useMediaType();
  const { lineItems, loyaltyProgram } = fragmentData || {};
  const dispatch = useEventTracking();
  const dispatchGlycerin = useTracking();

  const isFilledBasket = lineItems && lineItems.length > 0;

  useEffect(() => {
    getBridge().setTitle(
      formatMessage({
        id: 'App.Basket.Title',
        defaultMessage: 'Warenkorb',
      }),
    );
    dispatch('cart', 'viewPage');
  }, [dispatch, formatMessage]);

  useEffect(() => {
    if (lineItems) {
      const glycerinProducts: GlycerinCartProduct[] = [];
      if (isFilledBasket) {
        lineItems.forEach((lineItem) => {
          const lineItemFragment = getFragmentData(LineItemFragmentFragmentDoc, lineItem);
          glycerinProducts.push(buildGlycerinCartProduct(lineItemFragment));
        });
      }
      dispatchGlycerin<GTMEventGlycerinDisplayCart>({
        event: 'DisplayCart',
        DisplayCartData: {
          products: glycerinProducts,
        },
        _clear: true,
      });
    }
  }, [dispatchGlycerin, isFilledBasket, lineItems]);

  return (
    <>
      {!isFilledBasket && (
        <>
          <div className="bg-color-grey-light mb-3 p-[1.25rem_0.75rem_1.75rem] md:p-[1.25rem_1.75rem_3rem]">
            <BasketEmpty loggedIn={loggedIn} />
            <LoginDialogButton showLogin={!loggedIn} showBackToShop={!isFilledBasket} />
          </div>
          <DirectOrder />
        </>
      )}
      {isFilledBasket && (
        <div data-testid="filled-basket">
          <div className="bg-color-grey-light p-1.5 lg:p-3">
            {lineItems?.map((lineItem) => (
              <BasketLineItem
                lineItemData={lineItem}
                key={getFragmentData(LineItemFragmentFragmentDoc, lineItem)?.id}
              />
            ))}
          </div>
          <div id="swogo-bundle-0" className="swogo-box" />
          <DirectOrder />
          {!isDesktop && <PaybackBox loyaltyProgramData={loyaltyProgram || {}} />}
          {!isDesktop && <JoeLoyalty loyaltyProgramData={loyaltyProgram || {}} />}
          {!isDesktop && <BuyBox data={fragmentData || undefined} />}
          {!isDesktop && <Seals />}
        </div>
      )}
    </>
  );
};
